import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/apiConfig';
import { socket } from '../config/socket';
import './KitchenBarPanel.css';

const KitchenBarPanel = () => {
  const { auth } = useAuth();
  
  // Estados para los productos filtrados
  const [productosCocina, setProductosCocina] = useState([]);
  const [productosBarra, setProductosBarra] = useState([]);
  
  // Estados para control de vista
  const [cocinaMini, setCocinaMini] = useState(false);
  const [barraMini, setBarraMini] = useState(false);
  
  // Estados para contadores
  const [contadoresCocina, setContadoresCocina] = useState({
    pendientes: 0,
    enPreparacion: 0,
    listos: 0
  });
  
  const [contadoresBarra, setContadoresBarra] = useState({
    pendientes: 0,
    enPreparacion: 0,
    listos: 0
  });
  
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actualizando, setActualizando] = useState(false);
  const [mensajeExito, setMensajeExito] = useState('');

  // tipoBarra en estado local: inicializado desde JWT (rápido) y actualizado desde backend (real-time)
  const [tipoBarra, setTipoBarra] = useState(auth?.user?.tipo_barra || null);

  useEffect(() => {
    if (!auth?.token) return;
    fetch(getApiUrl('/barras/mi-barra'), {
      headers: { 'Authorization': `Bearer ${auth.token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(barra => {
        // barra puede ser null (sin asignar) o { id, nombre, tipo }
        setTipoBarra(barra?.tipo ?? null);
      })
      .catch(() => {}); // Si falla, se mantiene el valor del JWT
  }, [auth?.token]);

  // Función para verificar si un estado es pendiente (normal o con sufijo _pagado)
  const esPendiente = (estado) => {
    return estado === 'pendiente' || estado === 'pendiente_pagado';
  };

  // Función para verificar si un estado es en_prep (normal o con sufijo _pagado)
  const esEnPreparacion = (estado) => {
    return estado === 'en_prep' || estado === 'en_prep_pagado';
  };

  // Función para verificar si un estado es listo (normal o con sufijo _pagado)
  const esListo = (estado) => {
    return estado === 'listo' || estado === 'listo_pagado';
  };
  
  // Función para verificar si un estado es relevante para mostrar en el panel
  const esEstadoRelevante = (estado) => {
    return esPendiente(estado) || esEnPreparacion(estado) || esListo(estado);
  };

  // Función para determinar si un producto es de cocina o barra basado en su categoría
  const esProductoCocina = (categoria) => {
    // Ajusta estas categorías según tu lógica de negocio
    const categoriasCocina = ['piqueos', 'entrada', 'postre', 'comida'];
    return categoriasCocina.some(c => categoria?.toLowerCase().includes(c));
  };
  
  const esProductoBarra = (categoria) => {
    // Ajusta estas categorías según tu lógica de negocio
    const categoriasBarra = ['bebida', 'trago', 'cervezas', 'botella', 'cóctel', 'promoción'];
    return categoriasBarra.some(c => categoria?.toLowerCase().includes(c));
  };

  // Función para cargar todos los productos pendientes, en preparación y listos
  const cargarProductos = useCallback(async () => {
    try {
      setLoading(true);
      console.log('URL de la API:', getApiUrl('/productos/cocina-barra'));
      console.log('Token de autenticación disponible:', !!auth?.token);
      
      const response = await fetch(getApiUrl('/productos/cocina-barra'), {
        headers: {
          'Authorization': `Bearer ${auth?.token}`
        }
      });
  
      if (!response.ok) {
        const errorData = await response.text();
        console.error('Respuesta de error del servidor:', errorData);
        throw new Error(`Error al cargar productos: ${response.status} ${response.statusText}`);
      }
  
      const data = await response.json();
      console.log('Datos recibidos:', data); // Para depuración
      
      // Filtrar productos por categoría
      const productosCocinaFiltrados = data.filter(p => 
        esProductoCocina(p.categoria) && esEstadoRelevante(p.estado)
      );
      
      const productosBarraFiltrados = data.filter(p => 
        esProductoBarra(p.categoria) && esEstadoRelevante(p.estado)
      );
      
      // Clasificar productos que no encajen en ninguna categoría específica
      const productosSinClasificar = data.filter(p => 
        !esProductoCocina(p.categoria) && !esProductoBarra(p.categoria) && esEstadoRelevante(p.estado)
      );
      
      // Por defecto asignar productos sin clasificar a cocina
      const todosCocinaFiltrados = [...productosCocinaFiltrados, ...productosSinClasificar];
      
      // Ordenar por tiempo (los más antiguos primero)
      const ordenarPorTiempo = (a, b) => new Date(a.tiempo_pedido) - new Date(b.tiempo_pedido);
      
      setProductosCocina(todosCocinaFiltrados.sort(ordenarPorTiempo));
      setProductosBarra(productosBarraFiltrados.sort(ordenarPorTiempo));
      
      // Calcular contadores para cocina
      setContadoresCocina({
        pendientes: todosCocinaFiltrados.filter(p => esPendiente(p.estado)).length,
        enPreparacion: todosCocinaFiltrados.filter(p => esEnPreparacion(p.estado)).length,
        listos: todosCocinaFiltrados.filter(p => esListo(p.estado)).length
      });
      
      // Calcular contadores para barra
      setContadoresBarra({
        pendientes: productosBarraFiltrados.filter(p => esPendiente(p.estado)).length,
        enPreparacion: productosBarraFiltrados.filter(p => esEnPreparacion(p.estado)).length,
        listos: productosBarraFiltrados.filter(p => esListo(p.estado)).length
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error detallado:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [auth?.token]);

  useEffect(() => {
    cargarProductos();

    const handleSocketUpdate = (data) => {
      console.log('Evento de socket recibido en KitchenBarPanel:', data);
      cargarProductos();
    };

    socket.on('pedidoCreado', handleSocketUpdate);
    socket.on('cambioEstadoComanda', handleSocketUpdate);

    // Limpiar suscripciones al desmontar
    return () => {
      socket.off('pedidoCreado', handleSocketUpdate);
      socket.off('cambioEstadoComanda', handleSocketUpdate);
    };
  }, [cargarProductos]);

  // Función para calcular tiempo transcurrido en formato legible
  const calcularTiempoTranscurrido = (timestamp) => {
    if (!timestamp) return '00:00:00';
    
    const ahora = new Date();
    const pedido = new Date(timestamp);
    const diferencia = Math.floor((ahora - pedido) / 1000); // diferencia en segundos
    
    const horas = Math.floor(diferencia / 3600);
    const minutos = Math.floor((diferencia % 3600) / 60);
    const segundos = diferencia % 60;
    
    return `${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}:${segundos.toString().padStart(2, '0')}`;
  };

  // Función para verificar si un pedido es directo (tiene ID de mesa 910100)
  const esPedidoDirecto = (producto) => {
    return producto.id_mesa === 910100 || producto.estado.includes('_pagado');
  };

  // Función para obtener el estado equivalente para pedidos directos
  const getEstadoEquivalentePedidoDirecto = (estado) => {
    // Si ya tiene _pagado, lo dejamos igual
    if (estado.includes('_pagado')) {
      return estado;
    }
    
    // Convertimos el estado normal a su equivalente para pedidos directos
    return `${estado}_pagado`;
  };

  // Función para cambiar el estado de un producto
  const cambiarEstadoProducto = async (idPedido, idDetalle, nuevoEstado) => {
    try {
      setActualizando(true);
      
      // Determinar si el pedido es directo (para usar el sufijo _pagado)
      const producto = [...productosCocina, ...productosBarra].find(p => p.id === idDetalle);
      const estadoAActualizar = esPedidoDirecto(producto) ? getEstadoEquivalentePedidoDirecto(nuevoEstado) : nuevoEstado;
      
      const response = await fetch(getApiUrl(`/pedidos/${idPedido}/detalles/${idDetalle}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify({ estado: estadoAActualizar })
      });
      
      if (!response.ok) {
        throw new Error('Error al actualizar el estado del producto');
      }
      
      // Obtener respuesta del servidor para actualizar timestamps correctamente
      const detalleActualizado = await response.json();
      
      // Actualizar datos locales
      const actualizarProductos = (productos) => {
        return productos.map(producto => {
          if (producto.id === idDetalle) {
            return {...producto, ...detalleActualizado};
          }
          return producto;
        });
      };
      
      // Verificar si el producto actualizado debe estar en cocina o barra
      const productoActualizado = {...producto, ...detalleActualizado};
      const esParaCocina = esProductoCocina(productoActualizado.categoria);
      const esParaBarra = esProductoBarra(productoActualizado.categoria);
      
      if (esParaCocina || (!esParaBarra && !esParaCocina)) {
        setProductosCocina(actualizarProductos(productosCocina));
        
        // Actualizar contadores
        const nuevosContadores = {...contadoresCocina};
        if (esPendiente(producto.estado)) nuevosContadores.pendientes--;
        if (esEnPreparacion(producto.estado)) nuevosContadores.enPreparacion--;
        if (esListo(producto.estado)) nuevosContadores.listos--;
        
        if (esPendiente(estadoAActualizar)) nuevosContadores.pendientes++;
        if (esEnPreparacion(estadoAActualizar)) nuevosContadores.enPreparacion++;
        if (esListo(estadoAActualizar)) nuevosContadores.listos++;
        
        setContadoresCocina(nuevosContadores);
      } else {
        setProductosBarra(actualizarProductos(productosBarra));
        
        // Actualizar contadores
        const nuevosContadores = {...contadoresBarra};
        if (esPendiente(producto.estado)) nuevosContadores.pendientes--;
        if (esEnPreparacion(producto.estado)) nuevosContadores.enPreparacion--;
        if (esListo(producto.estado)) nuevosContadores.listos--;
        
        if (esPendiente(estadoAActualizar)) nuevosContadores.pendientes++;
        if (esEnPreparacion(estadoAActualizar)) nuevosContadores.enPreparacion++;
        if (esListo(estadoAActualizar)) nuevosContadores.listos++;
        
        setContadoresBarra(nuevosContadores);
      }
      
      mostrarMensajeExito(`Producto actualizado a estado: ${estadoAActualizar.replace('_', ' ')}`);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setActualizando(false);
    }
  };

  // Función para determinar los botones de acción disponibles según el estado actual
  const getBotonesAccion = (producto) => {
    // Extraer el estado base sin el sufijo _pagado
    const estadoBase = producto.estado.replace('_pagado', '');
    
    switch (estadoBase) {
      case 'pendiente':
        return (
          <button 
            onClick={() => cambiarEstadoProducto(producto.id_pedido, producto.id, 'en_prep')}
            className="btn-en-preparacion"
            disabled={actualizando}
          >
            EN PREPARACIÓN
          </button>
        );
      case 'en_prep':
        return (
          <button 
            onClick={() => cambiarEstadoProducto(producto.id_pedido, producto.id, 'listo')}
            className="btn-listo"
            disabled={actualizando}
          >
            LISTO
          </button>
        );
      // case 'listo':
        // return (
        //   <button 
        //     onClick={() => cambiarEstadoProducto(producto.id_pedido, producto.id, 'entregado')}
        //     className="btn-entregado"
        //     disabled={actualizando}
        //   >
        //     ENTREGADO
        //   </button>
        // );
      default:
        return null;
    }
  };

  const mostrarMensajeExito = (mensaje) => {
    setMensajeExito(mensaje);
    setTimeout(() => {
      setMensajeExito('');
    }, 3000);
  };

  // Función para obtener una versión resumida del número de mesa/pedido
  const getMesaPedidoResumido = (producto) => {
    if (producto.id_mesa === 910100) {
      return `PD:${producto.id_pedido}`;
    }
    
    if (producto.numero_mesa === undefined || producto.numero_mesa === null) {
      return `P:${producto.id_pedido}`;
    }
    
    // Mostramos Mesa (Piso) y el número de Pedido
    let mesaLabel = `M:${producto.numero_mesa}`;
    if (producto.numero_piso) {
      mesaLabel += ` (P°${producto.numero_piso})`;
    }

    if (producto.id_pedido) {
      return `${mesaLabel} P:${producto.id_pedido}`;
    }
    
    return mesaLabel;
  };

  // if (loading) {
  //   return <div className="loading">Cargando panel de cocina y barra...</div>;
  // }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  // Función para determinar la clase de tiempo según su duración
  const getClaseTiempo = (timestamp) => {
    if (!timestamp) return 'tiempo-normal';
    
    const ahora = new Date();
    const pedido = new Date(timestamp);
    const diferencia = Math.floor((ahora - pedido) / 1000); // diferencia en segundos
    
    if (diferencia > 900) { // Más de 15 minutos (900 segundos)
      return 'tiempo-critico';
    } else if (diferencia > 600) { // Más de 10 minutos (600 segundos)
      return 'tiempo-advertencia';
    }
    
    return 'tiempo-normal';
  };

  // Función para determinar si un pedido es directo o pagado para aplicar clase especial
  const getClasePedido = (producto) => {
    return esPedidoDirecto(producto) ? 'pedido-directo' : '';
  };

  // ── Visibilidad de secciones según rol y tipo de barra asignada ─────────────
  const rolUsuario = auth?.user?.rol;
  // tipoBarra viene del useState (actualizado en tiempo real desde /barras/mi-barra)

  const esCocinaOBartender = ['COCINERO', 'BARTENDER'].includes(rolUsuario);
  const esMixto = !tipoBarra || tipoBarra === 'mixto';

  // COCINERO: siempre ve COCINA. Ve BARRA solo si su barra es mixto.
  // BARTENDER: siempre ve BARRA. Ve COCINA solo si su barra es mixto.
  // Otros roles (ENCARGADO, MOZO): ven ambas secciones siempre.
  const mostrarCocina = !esCocinaOBartender || rolUsuario === 'COCINERO' || esMixto;
  const mostrarBarra  = !esCocinaOBartender || rolUsuario === 'BARTENDER' || esMixto;

  return (
    <div className="kitchen-bar-panel">
      {mensajeExito && (
        <div className="mensaje-exito">
          {mensajeExito}
        </div>
      )}

      {mostrarCocina && <div className={`panel-section cocina ${cocinaMini ? 'minimizado' : ''}`}>
        <div className="panel-header">
          <h2>COCINA</h2>
          <div className="panel-stats">
            <span>Pendientes: {contadoresCocina.pendientes}</span>
            <span>En preparación: {contadoresCocina.enPreparacion}</span>
            <span>Listos: {contadoresCocina.listos}</span>
          </div>
          <button 
            className="btn-minimizar"
            onClick={() => {
              setCocinaMini(!cocinaMini);
              setBarraMini(false); // Si minimizamos cocina, expandimos barra
            }}
          >
            {cocinaMini ? 'Expandir' : 'Minimizar'}
          </button>
        </div>
        
        {!cocinaMini && (
          <div className="panel-content">
            <table className="tabla-productos">
              <thead>
                <tr>
                  <th>MESA/PE</th>
                  <th>PRODUCTO</th>
                  <th>ESTADO</th>
                  <th>TIEMPO/P</th>
                  <th>NOTAS</th>
                  <th>ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {productosCocina.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-productos">No hay productos pendientes en cocina</td>
                  </tr>
                ) : (
                  productosCocina.map(producto => (
                    <tr 
                      key={producto.id} 
                      className={`estado-${producto.estado.replace('_', '-')} ${getClasePedido(producto)}`}
                      data-tiempo-transcurrido={getClaseTiempo(producto.tiempo_pedido) === 'tiempo-critico' ? 'critico' : 'normal'}
                    >
                      <td>{getMesaPedidoResumido(producto)}</td>
                      <td>{producto.nombre_producto}</td>
                      <td>{producto.estado.replace('_', ' ')}</td>
                      <td className={getClaseTiempo(producto.tiempo_pedido)}>
                        {calcularTiempoTranscurrido(producto.tiempo_pedido)}
                      </td>
                      <td>
                        {producto.notas ? (
                          <div className="notas-producto-container">
                            <span className="tiene-notas">Ver nota</span>
                            <div className="nota-tooltip">
                              {producto.notas}
                            </div>
                          </div>
                        ) : 'S/N'}
                      </td>
                      <td className="acciones">{getBotonesAccion(producto)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>}

      {mostrarBarra && <div className={`panel-section barra ${barraMini ? 'minimizado' : ''}`}>
        <div className="panel-header">
          <h2>BARRA</h2>
          <div className="panel-stats">
            <span>Pendientes: {contadoresBarra.pendientes}</span>
            <span>En preparación: {contadoresBarra.enPreparacion}</span>
            <span>Listos: {contadoresBarra.listos}</span>
          </div>
          <button 
            className="btn-minimizar" 
            onClick={() => {
              setBarraMini(!barraMini);
              setCocinaMini(false); // Si minimizamos barra, expandimos cocina
            }}
          >
            {barraMini ? 'Expandir' : 'Minimizar'}
          </button>
        </div>
        
        {!barraMini && (
          <div className="panel-content">
            <table className="tabla-productos">
              <thead>
                <tr>
                  <th>MESA/PE</th>
                  <th>PRODUCTO</th>
                  <th>ESTADO</th>
                  <th>TIEMPO/P</th>
                  <th>NOTAS</th>
                  <th>ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {productosBarra.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-productos">No hay productos pendientes en barra</td>
                  </tr>
                ) : (
                  productosBarra.map(producto => (
                    <tr 
                      key={producto.id} 
                      className={`estado-${producto.estado.replace('_', '-')} ${getClasePedido(producto)}`}
                      data-tiempo-transcurrido={getClaseTiempo(producto.tiempo_pedido) === 'tiempo-critico' ? 'critico' : 'normal'}
                    >
                      <td>{getMesaPedidoResumido(producto)}</td>
                      <td>{producto.nombre_producto}</td>
                      <td>{producto.estado.replace('_', ' ')}</td>
                      <td className={getClaseTiempo(producto.tiempo_pedido)}>
                        {calcularTiempoTranscurrido(producto.tiempo_pedido)}
                      </td>
                      <td>
                        {producto.notas ? (
                          <div className="notas-producto-container">
                            <span className="tiene-notas">Ver nota</span>
                            <div className="nota-tooltip">
                              {producto.notas}
                            </div>
                          </div>
                        ) : 'S/N'}
                      </td>
                      <td className="acciones">{getBotonesAccion(producto)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>}
    </div>
  );
};

export default KitchenBarPanel;